import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, Loader2, CreditCard, AlertCircle, Calendar, MapPin, Users, Home, ArrowLeft, Search, UserCheck, Lock, ChevronDown, Banknote, Copy, Mail } from 'lucide-react';
import { toast } from 'sonner';
import PaymentCheckout from '../components/PaymentCheckout';
import { FORM_TEMPLATES, DEFAULT_FORM_THEME, getFormTemplate } from '../data/formTemplates';
import { format, parseISO } from 'date-fns';
import { NotFoundPage, CampaignArchivedPage, RegistrationClosedPage } from './ErrorPages';
import { usePublicForm } from '../hooks/usePublicForm';
import PhoneInput from '../components/ui/PhoneInput';
import {
  isFieldHidden, isFieldDisabledByVisibility, applyTextCase, sortedChoiceOptions,
  sanitizeTextInput, wordCount, getChoiceLayoutClass, resolveOptionValue,
} from '../utils/fieldProperties';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
// Convert server-relative upload URLs to absolute (otherwise <img> resolves them against the frontend origin)
const toAbsUrl = (url) => url?.startsWith('/api/uploads/') ? `${API_BASE_URL}${url}` : (url || '');

const PublicRegistration = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [error, setError] = useState(null);

  // Offline Payment State
  const [paymentMethod, setPaymentMethod] = useState('online'); // 'online' or 'offline'
  const [offlineOtp, setOfflineOtp] = useState(null);
  const [showOfflineSuccess, setShowOfflineSuccess] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // 1-indexed for multi-page forms
  const pageHistoryRef = React.useRef([]); // navigation stack for accurate Previous with page rules

  // Closed Group Campaign State
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [verifyingMember, setVerifyingMember] = useState(false);

  // Get form theme from campaign settings or use default
  const getFormTheme = () => {
    const templateId = campaign?.form_config?.template_id;
    if (templateId) {
      return getFormTemplate(templateId).theme;
    }
    return campaign?.form_config?.theme || DEFAULT_FORM_THEME;
  };

  // Check if campaign is closed group type
  const isClosedGroup = campaign?.campaign_type === 'closed_group';

  // Whether this campaign will collect money. Determines whether we force the
  // Phone field to be required (gateways reject orders with a blank phone).
  const isPaidCampaign = !!(
    campaign?.payment_account_id ||
    (campaign?.form_config?.base_price > 0) ||
    (Array.isArray(campaign?.form_config?.pricing_rules) && campaign.form_config.pricing_rules.length > 0)
  );

  useEffect(() => {
    fetchCampaignData();
  }, [slug]);

  // Fetch members when campaign is loaded and is closed group
  useEffect(() => {
    if (isClosedGroup && campaign?.id) {
      fetchMembers();
    }
  }, [isClosedGroup, campaign?.id]);

  const fetchCampaignData = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/public/campaigns/${slug}`);
      const campaignData = response.data;

      // Event must be activated for the registration URL to open. Draft / closed events
      // are gated here so the public form never renders — backend rejects submit anyway.
      if (campaignData.status !== 'active') {
        setError('not_active');
        setCampaign(campaignData);
        setLoading(false);
        return;
      }

      // If registration is disabled, show closed message
      if (campaignData.registration_enabled === false) {
        setError('registration_closed');
        setCampaign(campaignData);
        setLoading(false);
        return;
      }

      // If using external form, redirect to external URL
      if (campaignData.use_external_form && campaignData.external_form_url) {
        window.location.href = campaignData.external_form_url;
        return;
      }

      setCampaign(campaignData);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('not_found');
      } else if (error.response?.status === 410) {
        setError('archived');
      } else {
        setError('error');
        toast.error('Campaign not found or not active');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (search = '') => {
    setLoadingMembers(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(
        `${API_BASE_URL}/api/campaigns/${campaign.id}/members/public${params}`
      );
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Failed to load member list');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Debounced search for members
  useEffect(() => {
    if (isClosedGroup && campaign?.id && memberSearch.length >= 2) {
      const timer = setTimeout(() => {
        fetchMembers(memberSearch);
      }, 300);
      return () => clearTimeout(timer);
    } else if (isClosedGroup && campaign?.id && memberSearch.length === 0) {
      fetchMembers();
    }
  }, [memberSearch, isClosedGroup, campaign?.id]);

  // Filter members based on search
  const filteredMembers = useMemo(() => {
    if (!memberSearch) return members;
    const search = memberSearch.toLowerCase();
    return members.filter(m => 
      m.name?.toLowerCase().includes(search) ||
      m.mobile_masked?.includes(search)
    );
  }, [members, memberSearch]);

  const selectMember = async (member) => {
    if (member.status === 'registered') {
      toast.error('This member has already registered');
      return;
    }

    setVerifyingMember(true);
    setShowMemberDropdown(false);

    try {
      // Verify member and get prefill data
      const response = await axios.get(
        `${API_BASE_URL}/api/campaigns/${campaign.id}/members/${member.id}/verify`
      );

      if (response.data.can_register) {
        setSelectedMember(member);
        
        // Prefill form data
        const prefillData = response.data.prefill_data || {};
        const newFormData = {
          ...formData,
          member_id: prefillData.member_id,
        };

        // Map prefill data to form fields
        const fields = campaign?.form_config?.fields || [];
        fields.forEach(field => {
          if (field.type === 'name' && prefillData.name) {
            // Try to split the name
            const nameParts = prefillData.name.split(' ');
            newFormData[`${field.id}_first`] = nameParts[0] || '';
            newFormData[`${field.id}_last`] = nameParts.slice(1).join(' ') || '';
            newFormData[field.id] = prefillData.name;
          } else if (field.type === 'email' && prefillData.email) {
            newFormData[field.id] = prefillData.email;
          } else if ((field.type === 'phone' || field.type === 'tel') && prefillData.mobile) {
            newFormData[field.id] = prefillData.mobile;
          }
        });

        // Also add custom fields if they exist
        if (prefillData.custom_fields) {
          Object.entries(prefillData.custom_fields).forEach(([key, value]) => {
            newFormData[key] = value;
          });
        }

        setFormData(newFormData);
        setMemberSearch(member.name);
        toast.success(`Welcome, ${member.name}! Your details have been prefilled.`);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This member has already registered');
      } else {
        toast.error('Failed to verify member');
      }
    } finally {
      setVerifyingMember(false);
    }
  };

  const clearSelectedMember = () => {
    setSelectedMember(null);
    setMemberSearch('');
    setFormData({});
  };

  const handleChange = (fieldId, value) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const calculateTotal = () => {
    if (!campaign?.form_config) return 0;
    
    const formConfig = campaign.form_config;
    const fields = formConfig.fields || [];
    // Check both field names for formula (pricing_formula is the new name, master_formula is legacy)
    const masterFormula = formConfig.pricing_formula || formConfig.master_formula || '';
    
    let total = 0;
    
    // If there's a formula defined, use it to calculate total
    if (masterFormula && masterFormula.trim()) {
      try {
        // Replace field references in formula with actual values
        let formulaStr = masterFormula;
        
        // Replace base_price reference
        formulaStr = formulaStr.replace(/\$\{base_price\}/g, parseFloat(formConfig.base_price) || 0);
        
        // Replace field references
        fields.forEach((field) => {
          const fieldValue = formData[field.id];
          let numericValue = 0;
          
          if (field.options && fieldValue) {
            // For fields with options, get the price modifier
            const selectedValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
            field.options.forEach((option) => {
              if (selectedValues.includes(option.value)) {
                numericValue += parseFloat(option.price_modifier) || 0;
              }
            });
          } else if (fieldValue && !isNaN(parseFloat(fieldValue))) {
            // For numeric fields, use the value directly
            numericValue = parseFloat(fieldValue);
          }
          
          // Replace field reference in formula
          const fieldRegex = new RegExp(`\\$\\{${field.id}\\}`, 'g');
          formulaStr = formulaStr.replace(fieldRegex, numericValue);
        });
        
        // Evaluate the formula safely (basic math only)
        // Replace common functions
        formulaStr = formulaStr.replace(/sum\(([^)]+)\)/gi, (match, args) => {
          const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
          return values.reduce((a, b) => a + b, 0);
        });
        formulaStr = formulaStr.replace(/avg\(([^)]+)\)/gi, (match, args) => {
          const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
          return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
        });
        formulaStr = formulaStr.replace(/min\(([^)]+)\)/gi, (match, args) => {
          const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
          return Math.min(...values);
        });
        formulaStr = formulaStr.replace(/max\(([^)]+)\)/gi, (match, args) => {
          const values = args.split(',').map(v => parseFloat(v.trim()) || 0);
          return Math.max(...values);
        });
        formulaStr = formulaStr.replace(/round\(([^)]+)\)/gi, (match, args) => {
          return Math.round(parseFloat(args) || 0);
        });
        
        // Handle if() function: if(condition, true_value, false_value)
        // Repeatedly process nested if() calls
        let maxIterations = 10;
        while (maxIterations-- > 0 && /if\s*\(/i.test(formulaStr)) {
          formulaStr = formulaStr.replace(
            /if\s*\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi,
            (match, condition, trueVal, falseVal) => {
              try {
                // Parse and evaluate the condition
                const conditionStr = condition.trim();
                let result = false;
                
                // Handle comparison operators
                if (conditionStr.includes('>=')) {
                  const [left, right] = conditionStr.split('>=').map(s => parseFloat(s.trim()) || 0);
                  result = left >= right;
                } else if (conditionStr.includes('<=')) {
                  const [left, right] = conditionStr.split('<=').map(s => parseFloat(s.trim()) || 0);
                  result = left <= right;
                } else if (conditionStr.includes('==')) {
                  const [left, right] = conditionStr.split('==').map(s => parseFloat(s.trim()) || 0);
                  result = left === right;
                } else if (conditionStr.includes('!=')) {
                  const [left, right] = conditionStr.split('!=').map(s => parseFloat(s.trim()) || 0);
                  result = left !== right;
                } else if (conditionStr.includes('>')) {
                  const [left, right] = conditionStr.split('>').map(s => parseFloat(s.trim()) || 0);
                  result = left > right;
                } else if (conditionStr.includes('<')) {
                  const [left, right] = conditionStr.split('<').map(s => parseFloat(s.trim()) || 0);
                  result = left < right;
                } else {
                  // If no comparison operator, evaluate as truthy/falsy
                  result = parseFloat(conditionStr) !== 0;
                }
                
                return result ? (parseFloat(trueVal.trim()) || 0) : (parseFloat(falseVal.trim()) || 0);
              } catch (e) {
                console.warn('Error evaluating if() condition:', e);
                return parseFloat(falseVal.trim()) || 0;
              }
            }
          );
        }
        
        // Safely evaluate basic math expression
        // Only allow numbers, operators, and parentheses
        if (/^[\d\s+\-*/().]+$/.test(formulaStr)) {
          total = eval(formulaStr);
        } else {
          console.warn('Invalid formula characters detected:', formulaStr);
          total = parseFloat(formConfig.base_price) || 0;
        }
      } catch (error) {
        console.error('Error evaluating formula:', error);
        total = parseFloat(formConfig.base_price) || 0;
      }
    } else {
      // No formula defined — auto-sum: base_price + sum of selected options' price_modifiers
      total = parseFloat(formConfig.base_price) || 0;
      fields.forEach((field) => {
        if (!Array.isArray(field.options) || field.options.length === 0) return;
        const fieldValue = formData[field.id];
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') return;
        const selectedValues = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
        field.options.forEach((option) => {
          if (selectedValues.includes(option.value)) {
            total += parseFloat(option.price_modifier) || 0;
          }
        });
      });
    }

    // Apply coupon discount
    if (couponApplied) {
      if (couponApplied.discount_type === 'percentage') {
        total = total * (1 - couponApplied.discount_value / 100);
      } else {
        total = Math.max(0, total - couponApplied.discount_value);
      }
    }

    return Math.max(0, total);
  };

  // Evaluate a single condition
  const evaluateCondition = (condition) => {
    // Check if this is a name field (which stores values in _first, _middle, _last sub-keys)
    const conditionField = campaign?.form_config?.fields?.find(f => f.id === condition.field_id);
    let fieldValue;
    
    if (conditionField?.type === 'name') {
      // For name fields, combine all parts to check if it's not empty
      const firstName = formData[`${condition.field_id}_first`] || '';
      const middleName = formData[`${condition.field_id}_middle`] || '';
      const lastName = formData[`${condition.field_id}_last`] || '';
      fieldValue = `${firstName}${middleName}${lastName}`.trim();
    } else {
      fieldValue = formData[condition.field_id];
    }
    
    const compareValue = condition.value;
    
    switch (condition.operator) {
      case 'equals':
        return fieldValue === compareValue;
      case 'not_equals':
        return fieldValue !== compareValue;
      case 'contains':
        return String(fieldValue || '').toLowerCase().includes(String(compareValue || '').toLowerCase());
      case 'greater_than':
        return parseFloat(fieldValue) > parseFloat(compareValue);
      case 'less_than':
        return parseFloat(fieldValue) < parseFloat(compareValue);
      case 'is_empty':
        return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || fieldValue === '';
      case 'is_not_empty':
        return fieldValue && (!Array.isArray(fieldValue) || fieldValue.length > 0) && fieldValue !== '';
      default:
        return false;
    }
  };

  // Evaluate field rules to determine field visibility and state
  const getFieldState = (fieldId) => {
    const fieldRules = campaign?.form_config?.field_rules || [];
    
    // Default state
    let state = {
      visible: true,
      required: null, // null means use field's default required setting
      disabled: false,
      value: null
    };

    // Process each rule
    fieldRules.forEach(rule => {
      // Check if this rule affects the current field
      const affectsField = rule.actions?.some(action => action.target_field_id === fieldId);
      if (!affectsField) return;

      // Evaluate conditions
      const conditions = rule.conditions || [];
      let conditionsMet = false;

      if (rule.match_type === 'all') {
        conditionsMet = conditions.every(evaluateCondition);
      } else {
        conditionsMet = conditions.some(evaluateCondition);
      }

      // Apply actions if conditions are met
      if (conditionsMet) {
        rule.actions?.forEach(action => {
          if (action.target_field_id !== fieldId) return;

          switch (action.action) {
            case 'show':
              state.visible = true;
              break;
            case 'hide':
              state.visible = false;
              break;
            case 'require':
              state.required = true;
              break;
            case 'enable':
              state.disabled = false;
              break;
            case 'disable':
              state.disabled = true;
              break;
            case 'set_value':
              state.value = action.value;
              break;
            default:
              break;
          }
        });
      } else {
        // If conditions are NOT met, and action is 'show', then hide the field
        rule.actions?.forEach(action => {
          if (action.target_field_id !== fieldId) return;
          if (action.action === 'show') {
            state.visible = false;
          }
        });
      }
    });

    return state;
  };

  // Check if a field should be visible based on field rules
  const isFieldVisible = (fieldId) => {
    return getFieldState(fieldId).visible;
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/campaigns/${campaign.id}/validate-coupon`, {
        code: couponCode.trim()
      });
      
      if (response.data.valid) {
        setCouponApplied({
          code: couponCode.trim(),
          discount_type: response.data.discount_type,
          discount_value: response.data.discount_value
        });
        toast.success('Coupon applied successfully!');
      } else {
        toast.error(response.data.error || 'Invalid coupon code');
        setCouponApplied(null);
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
      setCouponApplied(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    toast.info('Coupon removed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For closed group, ensure member is selected
    if (isClosedGroup && !selectedMember) {
      toast.error('Please select your name from the member list');
      return;
    }

    setSubmitting(true);

    try {
      // Get authoritative price from backend — prevents "Amount mismatch" on submit
      // when the form has pricing_rules the frontend doesn't evaluate
      let totalAmount = calculateTotal();
      try {
        const priceResp = await axios.post(
          `${API_BASE_URL}/api/campaigns/${campaign.id}/calculate-price`,
          { form_data: formData, coupon_code: couponApplied?.code || null }
        );
        if (priceResp.data && typeof priceResp.data.total === 'number') {
          totalAmount = priceResp.data.total;
        }
      } catch (priceErr) {
        // Fall back to local calculation if backend price check fails
        console.warn('Backend price calculation failed, using local total:', priceErr);
      }

      // Tell the server which fields the registrant never saw (hidden by conditional
      // rules), so it doesn't enforce "required" on them. The client is the source of
      // truth for visibility — this prevents false required errors from any drift
      // between the client and server rule engines.
      const allFormFields = campaign?.form_config?.fields || [];
      const hiddenFieldIds = allFormFields
        .filter((f) => f.id && !isFieldVisible(f.id))
        .map((f) => f.id);

      // Include member_id for closed group campaigns
      const submitData = {
        campaign_id: campaign.id,
        form_data: formData,
        total_amount: totalAmount,
        coupon_code: couponApplied?.code || null,
        hidden_field_ids: hiddenFieldIds
      };

      if (isClosedGroup && selectedMember) {
        submitData.member_id = selectedMember.id;
      }

      // The campaign may be configured as offline-only at the campaign level
      // (campaign.payment_collection_method === 'offline'), in which case the
      // payment-method toggle isn't shown and the local `paymentMethod` state
      // stays at its 'online' default. Treat that as offline too.
      const effectivePaymentMethod =
        campaign.payment_collection_method === 'offline' ? 'offline' : paymentMethod;

      // Check if offline payment is selected
      if (effectivePaymentMethod === 'offline' && totalAmount > 0) {
        // Submit offline registration
        const response = await axios.post(`${API_BASE_URL}/api/registrations/offline/submit`, submitData);
        const regData = response.data;
        
        setRegistrationId(regData.registration_id);
        setOfflineOtp(regData.otp);
        setShowOfflineSuccess(true);
        toast.success('Registration submitted! Check your email for the OTP.');
        return;
      }
      
      // Submit registration first (online payment flow)
      const response = await axios.post(`${API_BASE_URL}/api/registrations/submit`, submitData);

      const regData = response.data;
      setRegistrationId(regData.id);

      // Resolve buyer fields. Form data is keyed by random field ids
      // (`field_abc123`), so we can't look up `formData.name` directly. Walk
      // form_config in two passes: canonical widget TYPES first, then plain
      // text/textarea/number fields whose LABEL hints at name/email/phone.
      const fields = campaign?.form_config?.fields || [];
      const findByType = (...types) => {
        const f = fields.find((x) => types.includes(x.type));
        if (!f) return '';
        if (f.type === 'name') {
          const first = formData[`${f.id}_first`] || '';
          const middle = formData[`${f.id}_middle`] || '';
          const last = formData[`${f.id}_last`] || '';
          const combined = [first, middle, last].filter(Boolean).join(' ').trim();
          return combined || formData[f.id] || '';
        }
        return formData[f.id] || '';
      };
      const findByLabel = (keywords) => {
        const f = fields.find((x) => {
          if (!x.label || x.type === 'section_header' || x.type === 'calculated') return false;
          const lbl = String(x.label).toLowerCase();
          return keywords.some((k) => lbl.includes(k));
        });
        return f ? (formData[f.id] || '') : '';
      };

      const resolvedName =
        findByType('name') ||
        findByLabel(['name', 'full name', 'first name']) ||
        formData.name || formData.full_name || formData.first_name ||
        selectedMember?.name || 'Guest';
      const resolvedEmail =
        findByType('email') ||
        findByLabel(['email']) ||
        formData.email || selectedMember?.email || '';
      const resolvedPhone =
        findByType('phone', 'tel') ||
        findByLabel(['phone', 'mobile', 'contact']) ||
        formData.phone || formData.mobile || selectedMember?.mobile || '';

      setRegistrationData({
        ...regData,
        buyer_name: resolvedName,
        buyer_email: resolvedEmail || 'guest@example.com',
        buyer_phone: resolvedPhone
      });

      // Check if payment is required
      if (totalAmount > 0 && campaign.payment_account_id) {
        // Show payment checkout
        setShowPayment(true);
      } else if (totalAmount > 0 && !campaign.payment_account_id) {
        // No payment account configured - mark as pending
        toast.warning('Payment configuration pending. Your registration is saved.');
        setRegistrationComplete(true);
      } else {
        // Free registration - auto confirm
        await axios.post(`${API_BASE_URL}/api/registrations/payment/callback`, {
          registration_id: regData.id,
          payment_id: `free_${Date.now()}`,
          payment_gateway: 'free',
          status: 'success',
          amount: 0
        });
        setRegistrationComplete(true);
        toast.success('Registration successful!');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePaymentSuccess = (paymentId, gatewayPaymentId) => {
    setRegistrationComplete(true);
    toast.success('Payment successful! Registration confirmed.');
  };

  const handlePaymentFailure = (errorCode) => {
    toast.error('Payment failed. Please try again.');
  };

  // Render member selection component for closed group campaigns
  const renderMemberSelection = () => {
    if (!isClosedGroup) return null;

    return (
      <div className="col-span-12 mb-6 pb-6 border-b border-slate-200">
        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-outfit font-semibold text-slate-900">Member Registration</h3>
              <p className="text-sm text-slate-600 font-jakarta">
                This is a closed group event. Please select your name from the list.
              </p>
            </div>
          </div>

          {selectedMember ? (
            // Show selected member
            <div className="bg-white rounded-lg p-4 border border-secondary/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 font-jakarta">{selectedMember.name}</p>
                    <p className="text-sm text-slate-500 font-jakarta">
                      {selectedMember.mobile_masked} • {selectedMember.email_masked || 'No email'}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSelectedMember}
                  className="text-slate-500 hover:text-accent"
                >
                  Change
                </Button>
              </div>
            </div>
          ) : (
            // Show member search
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => {
                    setMemberSearch(e.target.value);
                    setShowMemberDropdown(true);
                  }}
                  onFocus={() => setShowMemberDropdown(true)}
                  placeholder="Search your name or mobile number..."
                  className="pl-10 pr-10 h-12 text-base rounded-xl border-slate-200 focus-visible:ring-primary bg-white"
                />
                {loadingMembers && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary animate-spin" />
                )}
                {!loadingMembers && (
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                )}
              </div>

              {/* Member Dropdown */}
              {showMemberDropdown && (
                <div className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-slate-200 max-h-72 overflow-y-auto">
                  {filteredMembers.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 font-jakarta">
                      {memberSearch.length >= 2 
                        ? 'No members found matching your search'
                        : 'Type at least 2 characters to search'}
                    </div>
                  ) : (
                    filteredMembers.map((member) => (
                      <button
                        key={member.id}
                        type="button"
                        onClick={() => selectMember(member)}
                        disabled={member.status === 'registered' || verifyingMember}
                        className={`w-full px-4 py-3 text-left border-b border-slate-100 last:border-b-0 transition-colors ${
                          member.status === 'registered'
                            ? 'bg-slate-50 cursor-not-allowed opacity-60'
                            : 'hover:bg-primary/5 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              member.status === 'registered' 
                                ? 'bg-slate-200' 
                                : 'bg-primary/10'
                            }`}>
                              {member.status === 'registered' ? (
                                <Lock className="w-5 h-5 text-slate-400" />
                              ) : (
                                <span className="text-primary font-semibold">
                                  {member.name?.charAt(0).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div>
                              <p className={`font-medium font-jakarta ${
                                member.status === 'registered' ? 'text-slate-500' : 'text-slate-900'
                              }`}>
                                {member.name}
                              </p>
                              <p className="text-sm text-slate-500 font-jakarta">
                                {member.mobile_masked}
                              </p>
                            </div>
                          </div>
                          {member.status === 'registered' && (
                            <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full font-jakarta">
                              Already Registered
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderField = (field) => {
    // Static Visibility property (admin set): "hide" removes the field entirely
    if (isFieldHidden(field)) {
      return null;
    }

    // Check field visibility based on field rules
    if (!isFieldVisible(field.id)) {
      return null;
    }

    const fieldState = getFieldState(field.id);
    // New width names (small/medium/large) + legacy (full/half/third) for back-compat.
    // Mobile-first: every partial width collapses to col-span-12 on phones to avoid
    // sub-200px fields that are unusable for typing on a touchscreen.
    const widthClass = {
      small:  'col-span-12 sm:col-span-6',
      medium: 'col-span-12 sm:col-span-8',
      large:  'col-span-12',
      full:   'col-span-12',
      half:   'col-span-12 sm:col-span-6',
      third:  'col-span-12 sm:col-span-4',
    }[field.width] || 'col-span-12';

    // For closed group, some fields may be pre-filled and read-only
    const isPrefilledField = isClosedGroup && selectedMember && (
      field.type === 'name' || field.type === 'email' || field.type === 'phone' || field.type === 'tel'
    );

    // Determine if field is required (field rules can override)
    let isRequired = fieldState.required !== null ? fieldState.required : field.required;
    // Phone fields must be required when the campaign collects payment —
    // Instamojo/Cashfree reject orders with a blank buyer phone. Override
    // any "optional" setting in this case so the registrant can't skip it.
    if ((field.type === 'phone' || field.type === 'tel') && isPaidCampaign) {
      isRequired = true;
    }
    const isDisabled = fieldState.disabled || isPrefilledField || isFieldDisabledByVisibility(field);

    if (field.type === 'section_header') {
      return (
        <div key={field.id} className="col-span-12 mb-4 mt-6">
          <h3 className="text-xl font-outfit font-semibold text-slate-900 mb-2">{field.label}</h3>
          {field.help_text && <p className="text-sm text-slate-600 font-jakarta">{field.help_text}</p>}
        </div>
      );
    }

    if (field.type === 'name') {
      const nameConfig = field.name_config || {};
      const showFirst = nameConfig.show_first_name !== false;
      const showMiddle = nameConfig.show_middle_name === true;
      const showLast = nameConfig.show_last_name !== false;
      const visibleCount = [showFirst, showMiddle, showLast].filter(Boolean).length;
      
      // Calculate column widths based on visible fields.
      // Mobile-first: 3-part names stack (full width) on phones; 2-part names use
      // half-width even on mobile (first + last are short enough to fit).
      const getColSpan = () => {
        if (visibleCount === 1) return 'col-span-12';
        if (visibleCount === 2) return 'col-span-12 sm:col-span-6';
        return 'col-span-12 sm:col-span-4';
      };
      const colSpan = getColSpan();

      return (
        <div key={field.id} className="col-span-12">
          <Label className="text-slate-700 font-jakarta font-medium mb-3 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <div className="grid grid-cols-12 gap-4">
            {showFirst && (
              <div className={colSpan}>
                <Label className="text-slate-600 font-jakarta text-sm mb-1.5 block">
                  {nameConfig.first_name_label || 'First Name'}
                </Label>
                <Input
                  type="text"
                  autoComplete="given-name"
                  value={formData[`${field.id}_first`] || ''}
                  onChange={(e) => handleChange(`${field.id}_first`, sanitizeTextInput(e.target.value, field))}
                  maxLength={field.max_length || undefined}
                  placeholder=""
                  required={isRequired}
                  readOnly={isDisabled}
                  className={`h-11 rounded-lg border-slate-200 focus-visible:ring-primary ${isDisabled ? 'bg-slate-50' : ''}`}
                />
              </div>
            )}
            {showMiddle && (
              <div className={colSpan}>
                <Label className="text-slate-600 font-jakarta text-sm mb-1.5 block">
                  {nameConfig.middle_name_label || 'Middle Name'}
                </Label>
                <Input
                  type="text"
                  autoComplete="additional-name"
                  value={formData[`${field.id}_middle`] || ''}
                  onChange={(e) => handleChange(`${field.id}_middle`, sanitizeTextInput(e.target.value, field))}
                  maxLength={field.max_length || undefined}
                  placeholder=""
                  readOnly={isDisabled}
                  className={`h-11 rounded-lg border-slate-200 focus-visible:ring-primary ${isDisabled ? 'bg-slate-50' : ''}`}
                />
              </div>
            )}
            {showLast && (
              <div className={colSpan}>
                <Label className="text-slate-600 font-jakarta text-sm mb-1.5 block">
                  {nameConfig.last_name_label || 'Last Name'}
                </Label>
                <Input
                  type="text"
                  autoComplete="family-name"
                  value={formData[`${field.id}_last`] || ''}
                  onChange={(e) => handleChange(`${field.id}_last`, sanitizeTextInput(e.target.value, field))}
                  maxLength={field.max_length || undefined}
                  placeholder=""
                  readOnly={isDisabled}
                  className={`h-11 rounded-lg border-slate-200 focus-visible:ring-primary ${isDisabled ? 'bg-slate-50' : ''}`}
                />
              </div>
            )}
          </div>
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    if (field.type === 'dropdown') {
      return (
        <div key={field.id} className={widthClass}>
          <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <select
            value={formData[field.id] || ''}
            onChange={(e) => handleChange(field.id, e.target.value)}
            required={isRequired}
            disabled={isDisabled}
            className="w-full h-11 rounded-lg border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:bg-slate-50 disabled:text-slate-600 disabled:cursor-not-allowed"
          >
            <option value="">Select an option</option>
            {sortedChoiceOptions(field).map((opt) => {
              const submitValue = resolveOptionValue(opt);
              return (
                <option key={opt.value ?? opt.label} value={submitValue}>
                  {opt.label}
                </option>
              );
            })}
          </select>
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    if (field.type === 'textarea') {
      const txt = formData[field.id] || '';
      const wc  = wordCount(txt);
      return (
        <div key={field.id} className={widthClass}>
          <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <textarea
            value={txt}
            onChange={(e) => handleChange(field.id, sanitizeTextInput(e.target.value, field))}
            placeholder={field.placeholder}
            required={isRequired}
            disabled={isDisabled}
            rows={4}
            maxLength={field.max_length || undefined}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:bg-slate-50 disabled:text-slate-600"
          />
          {(field.max_length || field.max_words) && (
            <p className="text-xs text-slate-400 font-jakarta mt-1 text-right space-x-2">
              {field.max_length && (
                <span>{txt.length} / {field.max_length} chars</span>
              )}
              {field.max_words && (
                <span className={wc > field.max_words ? 'text-red-500' : ''}>
                  {wc} / {field.max_words} words
                </span>
              )}
            </p>
          )}
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    if (field.type === 'checkbox') {
      // Check if this checkbox has multiple options
      const hasOptions = field.options && Array.isArray(field.options) && field.options.length > 0;
      
      if (hasOptions) {
        // Multi-select checkbox with options
        const selected   = formData[field.id] || [];
        const maxSel     = field.max_selections;
        const atMax      = maxSel && selected.length >= maxSel;
        return (
          <div key={field.id} className={widthClass}>
            <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
              {field.label} {isRequired && <span className="text-accent">*</span>}
              {maxSel && (
                <span className={`ml-2 text-xs font-normal ${atMax ? 'text-amber-600' : 'text-slate-500'}`}>
                  ({selected.length}/{maxSel} selected)
                </span>
              )}
            </Label>
            <div className={getChoiceLayoutClass(field.column_layout)}>
              {sortedChoiceOptions(field).map((opt) => {
                const submitValue = resolveOptionValue(opt);
                const isChecked   = selected.includes(submitValue);
                const lockedOut   = atMax && !isChecked;
                return (
                  // 44px-min row, whole row tappable on touch.
                  <label
                    key={opt.value ?? opt.label}
                    htmlFor={`${field.id}-${opt.value ?? opt.label}`}
                    className={`flex items-center gap-3 min-h-[44px] py-1 cursor-pointer ${(isDisabled || lockedOut) ? 'cursor-not-allowed' : ''}`}
                  >
                    <input
                      type="checkbox"
                      id={`${field.id}-${opt.value ?? opt.label}`}
                      checked={isChecked}
                      onChange={(e) => {
                        const current = formData[field.id] || [];
                        const newValue = e.target.checked
                          ? [...current, submitValue]
                          : current.filter(v => v !== submitValue);
                        handleChange(field.id, newValue);
                      }}
                      disabled={isDisabled || lockedOut}
                      title={lockedOut ? `Maximum ${maxSel} selections allowed` : undefined}
                      style={{ accentColor: '#7c3aed', width: 22, height: 22, appearance: 'auto', opacity: (isDisabled || lockedOut) ? 0.6 : 1, flexShrink: 0 }}
                    />
                    <span className={`font-jakarta select-none ${(isDisabled || lockedOut) ? 'text-slate-400' : 'text-slate-600'}`}>
                      {opt.label}
                    </span>
                  </label>
                );
              })}
            </div>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
          </div>
        );
      } else {
        // Single boolean checkbox (no options)
        return (
          <div key={field.id} className={widthClass}>
            <label htmlFor={field.id} className="flex items-start gap-3 cursor-pointer min-h-[44px] py-1">
              <input
                type="checkbox"
                id={field.id}
                checked={formData[field.id] || false}
                onChange={(e) => handleChange(field.id, e.target.checked)}
                required={isRequired}
                style={{ accentColor: '#7c3aed', width: 22, height: 22, appearance: 'auto', marginTop: 4, flexShrink: 0 }}
              />
              <span className="text-slate-700 font-jakarta font-medium select-none">
                {field.label} {isRequired && <span className="text-accent">*</span>}
              </span>
            </label>
            {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1 ml-7">{field.help_text}</p>}
          </div>
        );
      }
    }

    if (field.type === 'radio') {
      return (
        <div key={field.id} className={widthClass}>
          <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <div className={getChoiceLayoutClass(field.column_layout)}>
            {sortedChoiceOptions(field).map((opt) => {
              const submitValue = resolveOptionValue(opt);
              return (
                // 44px-min row, whole row tappable on touch.
                <label
                  key={opt.value ?? opt.label}
                  htmlFor={`${field.id}-${opt.value ?? opt.label}`}
                  className={`flex items-center gap-3 min-h-[44px] py-1 cursor-pointer ${isDisabled ? 'cursor-not-allowed' : ''}`}
                >
                  <input
                    type="radio"
                    id={`${field.id}-${opt.value ?? opt.label}`}
                    name={field.id}
                    value={submitValue}
                    checked={formData[field.id] === submitValue}
                    onChange={(e) => handleChange(field.id, e.target.value)}
                    required={isRequired}
                    disabled={isDisabled}
                    style={{ accentColor: '#7c3aed', width: 22, height: 22, appearance: 'auto', flexShrink: 0 }}
                  />
                  <span className={`font-jakarta select-none ${isDisabled ? 'text-slate-400' : 'text-slate-600'}`}>
                    {opt.label}
                  </span>
                </label>
              );
            })}
          </div>
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    if (field.type === 'file_upload') {
      const stored   = formData[field.id];
      // stored is either: null/undefined, an in-progress { uploading: true, name }, or a final { url, name, size }
      const uploading = stored?.uploading;
      const fileName  = stored?.name || null;
      const fileUrl   = stored?.url  || null;

      const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Show in-progress state
        handleChange(field.id, { uploading: true, name: file.name });
        try {
          const fd = new FormData();
          fd.append('file', file);
          const resp = await axios.post(
            `${API_BASE_URL}/api/uploads/public`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          if (resp.data?.success) {
            handleChange(field.id, {
              url:  resp.data.url,
              name: resp.data.filename,
              size: resp.data.size
            });
            toast.success('File uploaded');
          } else {
            throw new Error('Upload failed');
          }
        } catch (err) {
          toast.error(err.response?.data?.detail || 'File upload failed');
          handleChange(field.id, null);
        }
      };

      return (
        <div key={field.id} className={widthClass}>
          <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <div className="flex items-center gap-3">
            <label
              htmlFor={`file-${field.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${uploading ? 'opacity-50 cursor-wait' : 'cursor-pointer hover:bg-primary/90'}`}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              )}
              {uploading ? 'Uploading...' : (fileUrl ? 'Change file' : 'Choose file')}
            </label>
            <input
              id={`file-${field.id}`}
              type="file"
              onChange={handleUpload}
              required={isRequired && !fileUrl}
              disabled={uploading}
              className="hidden"
            />
            {fileUrl ? (
              <a
                href={`${API_BASE_URL}${fileUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline truncate max-w-[250px]"
              >
                {fileName}
              </a>
            ) : (
              <span className="text-sm text-slate-600 font-jakarta truncate">
                {fileName || 'No file chosen'}
              </span>
            )}
          </div>
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    if (field.type === 'terms_checkbox') {
      return (
        <div key={field.id} className={widthClass}>
          <label htmlFor={field.id} className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              id={field.id}
              checked={formData[field.id] || false}
              onChange={(e) => handleChange(field.id, e.target.checked)}
              required={isRequired}
              style={{ accentColor: '#7c3aed', width: 18, height: 18, appearance: 'auto', marginTop: 4 }}
            />
            <span className="text-slate-700 font-jakarta text-sm">
              {field.label} {isRequired && <span className="text-accent">*</span>}
            </span>
          </label>
        </div>
      );
    }

    // Handle phone/mobile number fields - numbers only
    if (field.type === 'phone' || field.type === 'tel') {
      const allowedCountries = field.phone_country_mode === 'allowed'
        && Array.isArray(field.phone_allowed_countries)
        && field.phone_allowed_countries.length > 0
          ? field.phone_allowed_countries
          : undefined;
      const defaultCountry = field.phone_default_country
        || (allowedCountries ? allowedCountries[0] : 'IN');
      const effectiveCountry = formData[`${field.id}_country`] || defaultCountry;
      return (
        <div key={field.id} className={widthClass}>
          <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <PhoneInput
            value={formData[field.id] || ''}
            onChange={(v) => handleChange(field.id, v)}
            countryCode={effectiveCountry}
            onCountryChange={(c) => handleChange(`${field.id}_country`, c)}
            required={isRequired}
            disabled={isDisabled}
            allowedCountries={allowedCountries}
          />
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    // Handle number fields - numbers only
    if (field.type === 'number') {
      return (
        <div key={field.id} className={widthClass}>
          <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <Input
            type="text"
            inputMode="decimal"
            pattern="[0-9]*"
            value={formData[field.id] || ''}
            onChange={(e) => {
              // Only allow numbers and decimal point
              const value = e.target.value.replace(/[^0-9.]/g, '');
              handleChange(field.id, value);
            }}
            placeholder={field.placeholder}
            required={isRequired}
            readOnly={isDisabled}
            min={field.min_value ?? field.min}
            max={field.max_value ?? field.max}
            step={field.step || 'any'}
            className={`h-11 rounded-lg border-slate-200 focus-visible:ring-primary ${isDisabled ? 'bg-slate-50' : ''}`}
          />
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    // Handle email fields - with email validation
    if (field.type === 'email') {
      return (
        <div key={field.id} className={widthClass}>
          <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
            {field.label} {isRequired && <span className="text-accent">*</span>}
          </Label>
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={formData[field.id] || ''}
            onChange={(e) => handleChange(field.id, sanitizeTextInput(e.target.value, field))}
            placeholder={field.placeholder || 'example@email.com'}
            required={isRequired}
            readOnly={isDisabled}
            maxLength={field.max_length || undefined}
            pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
            className={`h-11 rounded-lg border-slate-200 focus-visible:ring-primary ${isDisabled ? 'bg-slate-50' : ''}`}
          />
          {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
        </div>
      );
    }

    return (
      <div key={field.id} className={widthClass}>
        <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
          {field.label} {isRequired && <span className="text-accent">*</span>}
        </Label>
        <Input
          type={field.type === 'phone' || field.type === 'tel' ? 'tel' : field.type}
          value={formData[field.id] || ''}
          onChange={(e) => handleChange(field.id, sanitizeTextInput(e.target.value, field))}
          placeholder={field.placeholder}
          required={isRequired}
          readOnly={isDisabled}
          maxLength={field.max_length || undefined}
          className={`h-11 rounded-lg border-slate-200 focus-visible:ring-primary ${isDisabled ? 'bg-slate-50' : ''}`}
        />
        {field.max_length && (
          <p className="text-xs text-slate-400 font-jakarta mt-1 text-right">
            {(formData[field.id] || '').length} / {field.max_length}
          </p>
        )}
        {field.help_text && <p className="text-xs text-slate-500 font-jakarta mt-1">{field.help_text}</p>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-jakarta">Loading event...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (error === 'not_found' || !campaign) {
    return <NotFoundPage />;
  }

  if (error === 'archived') {
    return <CampaignArchivedPage campaignName={campaign?.name} />;
  }

  // Event not yet activated — block the form entirely so registrations cannot start
  if (error === 'not_active') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-8 max-w-lg w-full text-center">
          {campaign?.logo_url && (
            <img
              src={toAbsUrl(campaign.logo_url)}
              alt={campaign.name}
              className="h-16 mx-auto mb-4 object-contain"
            />
          )}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-4">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
            Registration Not Available
          </h2>
          <p className="text-slate-600 font-jakarta mb-6">
            {campaign?.name ? `"${campaign.name}"` : 'This event'} is not currently open for registration.
            Please check back once the organizer activates the event.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4 border-t border-slate-100">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-slate-300 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="bg-primary hover:bg-primary-hover"
            >
              <Home className="w-4 h-4 mr-2" />
              Browse Events
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Registration closed state - show dedicated page
  if (error === 'registration_closed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-8 max-w-lg w-full text-center">
          {campaign.logo_url && (
            <img
              src={toAbsUrl(campaign.logo_url)}
              alt={campaign.name}
              className="h-16 mx-auto mb-4 object-contain"
            />
          )}
          <RegistrationClosedPage 
            campaignName={campaign?.name}
            endDate={campaign?.registration_end_date}
          />
          <p className="text-sm text-slate-500 mt-4">
            For inquiries, please contact the event organizer.
          </p>
          
          {/* Navigation buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6 pt-4 border-t border-slate-100">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="border-slate-300 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button
              onClick={() => navigate('/')}
              className="bg-primary hover:bg-primary-hover"
            >
              <Home className="w-4 h-4 mr-2" />
              Browse Events
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const theme = getFormTheme();
  const headerImage = campaign?.form_config?.header_image;
  const bannerImage = campaign?.form_config?.banner_image;

  // Payment Checkout View
  if (showPayment && registrationData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12 px-4">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2">
              Complete Payment
            </h1>
            <p className="text-slate-600 font-jakarta">
              for {campaign.name}
            </p>
          </div>

          <PaymentCheckout
            campaignId={campaign.id}
            registrationId={registrationData.id}
            amount={registrationData.total_amount}
            buyerName={registrationData.buyer_name}
            buyerEmail={registrationData.buyer_email}
            buyerPhone={registrationData.buyer_phone}
            purpose={`Registration for ${campaign.name}`}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentFailure={handlePaymentFailure}
          />

          <div className="mt-6 text-center">
            <Button 
              variant="ghost" 
              onClick={() => setShowPayment(false)}
              className="text-slate-500"
            >
              ← Back to form
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Offline Registration Success View
  if (showOfflineSuccess && offlineOtp) {
    const copyOtp = () => {
      navigator.clipboard.writeText(offlineOtp);
      toast.success('OTP copied to clipboard!');
    };

    // Extract registrant name from form data
    const getRegistrantName = () => {
      const fields = campaign?.form_config?.fields || [];
      for (const field of fields) {
        if (field.type === 'name') {
          const firstName = formData[`${field.id}_first`] || '';
          const lastName = formData[`${field.id}_last`] || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName) return fullName;
        }
      }
      // Fallback to any name-like field
      if (formData.name) return formData.name;
      if (formData.full_name) return formData.full_name;
      return '';
    };

    const registrantName = getRegistrantName();
    const eventName = campaign?.name || 'the event';

    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white flex items-center justify-center p-6">
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-xl p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-outfit font-bold text-slate-900 mb-2">
            {registrantName ? `Hey ${registrantName}!` : 'Registration Submitted!'}
          </h2>
          
          <p className="text-slate-600 font-jakarta mb-6">
            You have successfully registered for <strong>{eventName}</strong>. 
            Please share the following OTP after paying at the Registration Counter to complete your registration.
          </p>
          
          {/* OTP Display */}
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-6 mb-6">
            <p className="text-white/80 text-sm mb-2 font-jakarta">Your Registration OTP</p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-4xl font-mono font-bold text-white tracking-widest">
                {offlineOtp}
              </span>
              <button
                onClick={copyOtp}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Copy OTP"
              >
                <Copy className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
          
          <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="w-4 h-4 text-primary" />
              <span>OTP has been emailed to your registered email</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CreditCard className="w-4 h-4 text-primary" />
              <span>Amount to pay: <strong className="text-primary">₹{calculateTotal().toFixed(2)}</strong></span>
            </div>
          </div>
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
            <p className="text-sm text-amber-800 font-jakarta">
              <strong>Important:</strong> Please save this OTP. You&apos;ll need to present it at the event venue along with the payment to complete your registration.
            </p>
          </div>
          
          <Button
            onClick={() => navigate('/')}
            className="mt-6 bg-primary hover:bg-primary-hover w-full"
          >
            <Home className="w-4 h-4 mr-2" />
            Return to Home
          </Button>
        </Card>
      </div>
    );
  }

  // Registration Complete View
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="bg-white rounded-xl border border-slate-100 shadow-card p-8 max-w-2xl w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-outfit font-bold text-slate-900 mb-3">
            Registration Successful!
          </h2>
          <p className="text-lg text-slate-600 font-jakarta mb-6">
            Thank you for registering for {campaign.name}
          </p>
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-slate-500 font-jakarta mb-1">Registration ID</p>
            <p className="text-lg font-mono font-semibold text-slate-900">{registrationId}</p>
          </div>
          <p className="text-sm text-slate-600 font-jakarta">
            A confirmation email with your receipt has been sent to your registered email address.
          </p>
        </Card>
      </div>
    );
  }

  // Registration Form View
  return (
    <div 
      className="min-h-screen py-4 sm:py-8 px-3 sm:px-4"
      style={{ 
        background: theme.page_background || theme.gradient || '#f8fafc',
        fontFamily: theme.font_family || 'Inter'
      }}
      onClick={() => setShowMemberDropdown(false)}
    >
      <div className="max-w-3xl mx-auto">
        {/* Form Banner — full-width image at natural aspect ratio, no overlay,
            shown above everything else. Rendered only if the admin uploaded one. */}
        {bannerImage && (
          <img
            src={toAbsUrl(bannerImage)}
            alt=""
            className="w-full h-auto block rounded-t-2xl mb-3 sm:mb-4"
            loading="eager"
          />
        )}

        {/* Header Image */}
        {headerImage && (
          <div className="relative h-48 md:h-64 rounded-t-2xl overflow-hidden mb-0">
            <img
              src={toAbsUrl(headerImage)}
              alt={campaign.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: theme.font_family }}>
                {campaign.name}
              </h1>
              <p className="text-white/90">{campaign.organization_name}</p>
              {isClosedGroup && (
                <span className="inline-flex items-center gap-1 mt-2 bg-secondary/90 text-white text-xs px-3 py-1 rounded-full">
                  <Lock className="w-3 h-3" />
                  Members Only Event
                </span>
              )}
            </div>
          </div>
        )}

        {/* Form Card */}
        <Card 
          className={`${headerImage ? 'rounded-t-none' : 'rounded-2xl'} border-0 shadow-2xl overflow-hidden`}
          style={{ 
            backgroundColor: theme.card_style === 'glass' 
              ? 'rgba(255,255,255,0.1)' 
              : '#ffffff',
            backdropFilter: theme.card_style === 'glass' ? 'blur(20px)' : 'none',
          }}
        >
          {/* Event Info Header (if no header image) */}
          {!headerImage && (
            <div 
              className="px-8 py-8 text-center border-b"
              style={{ 
                backgroundColor: theme.card_style === 'glass' ? 'transparent' : theme.background_color,
                borderColor: theme.card_style === 'glass' ? 'rgba(255,255,255,0.1)' : '#e2e8f0'
              }}
            >
              {(campaign.show_event_logo ?? true) && campaign.logo_url && (
                <img
                  src={toAbsUrl(campaign.logo_url)}
                  alt={campaign.name}
                  className="h-20 mx-auto mb-4 object-contain rounded-lg"
                />
              )}
              <h1
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ color: theme.text_color, fontFamily: theme.font_family }}
              >
                {campaign.name}
              </h1>
              {(campaign.show_organization_name ?? true) && (
                <p style={{ color: theme.accent_color || theme.primary_color }} className="font-medium">
                  {campaign.organization_name}
                </p>
              )}
              {isClosedGroup && (
                <span className="inline-flex items-center gap-1 mt-3 bg-secondary/10 text-secondary text-sm px-3 py-1 rounded-full">
                  <Lock className="w-4 h-4" />
                  Members Only Event
                </span>
              )}
              {(campaign.show_description ?? true) && campaign.description && (
                <p className="mt-3 max-w-xl mx-auto" style={{ color: theme.text_color, opacity: 0.8 }}>
                  {campaign.description}
                </p>
              )}
              
              {/* Event Date/Time Info */}
              {(campaign.start_date || campaign.end_date) && (
                <div className="flex items-center justify-center gap-4 mt-4 flex-wrap">
                  {campaign.start_date && (
                    <div className="flex items-center gap-2 text-sm" style={{ color: theme.text_color, opacity: 0.7 }}>
                      <Calendar className="w-4 h-4" />
                      <span>{format(parseISO(campaign.start_date), 'MMMM dd, yyyy')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Form Content */}
          <div className="p-5 sm:p-8" style={{ backgroundColor: theme.card_style === 'glass' ? 'transparent' : '#ffffff' }}>
            <h2 
              className="text-xl font-bold mb-6 pb-4 border-b"
              style={{ 
                color: theme.card_style === 'glass' ? theme.text_color : '#1e293b',
                borderColor: theme.card_style === 'glass' ? 'rgba(255,255,255,0.1)' : '#e2e8f0'
              }}
            >
              Registration Details
            </h2>
          
            {campaign.form_config && campaign.form_config.fields ? (() => {
              // ── Multi-page form setup ────────────────────────────────────
              const allFields  = campaign.form_config.fields || [];
              const pageBreaks = (campaign.form_config.page_breaks || [])
                .slice()
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
              const pageRules  = campaign.form_config.page_rules || [];

              // Split fields into pages by break positions.
              // Each break's `position` = number of fields before the break.
              // pages[0] = fields 0..break[0].position-1, pages[1] = between break[0] and break[1], etc.
              const pages = [];
              let lastPos = 0;
              for (const b of pageBreaks) {
                const pos = Math.max(lastPos, Math.min(b.position ?? allFields.length, allFields.length));
                pages.push({
                  fields: allFields.slice(lastPos, pos),
                  title: b.title || `Page ${pages.length + 1}`,
                  description: b.description || ''
                });
                lastPos = pos;
              }
              // Final page (after last break or all fields if no breaks)
              pages.push({
                fields: allFields.slice(lastPos),
                title: pageBreaks.length === 0 ? 'Page 1' : (pageBreaks[pageBreaks.length - 1]?.next_title || `Page ${pages.length + 1}`),
                description: ''
              });

              const totalPages   = pages.length;
              const isMultiPage  = totalPages > 1;
              const pageIndex    = Math.max(0, Math.min(currentPage - 1, totalPages - 1));
              const currentFields = pages[pageIndex]?.fields || [];
              const pageMeta     = pages[pageIndex] || { title: '', description: '' };
              const isLastPage   = currentPage >= totalPages;
              const isFirstPage  = currentPage <= 1;

              // Returns whether a single field's stored value satisfies its "required"
              // semantics. Pure — no side effects, safe to call during render. Returns
              // null when the field is hidden / not-required (irrelevant). Returns true
              // when filled, false when empty-but-required.
              const checkFieldFilled = (field) => {
                if (!isFieldVisible(field.id)) return null;
                const fieldState = getFieldState(field.id);
                const ruleRequired = fieldState.required !== null ? fieldState.required : field.required;
                const fieldRequired = ruleRequired ||
                  ((field.type === 'phone' || field.type === 'tel') && isPaidCampaign);
                if (!fieldRequired) return null;
                if (field.type === 'section_header') return null;

                if (field.type === 'name') {
                  const nc = field.name_config || {};
                  const showFirst = nc.show_first_name !== false;
                  const first  = (formData[`${field.id}_first`]  || '').trim();
                  const middle = (formData[`${field.id}_middle`] || '').trim();
                  const last   = (formData[`${field.id}_last`]   || '').trim();
                  return !(showFirst ? !first : (!first && !middle && !last));
                }

                const val = formData[field.id];

                if (field.type === 'file_upload') {
                  return !!(val && val.url);
                }

                const isBoolCheckbox =
                  field.type === 'terms_checkbox' ||
                  (field.type === 'checkbox' && !(field.options && field.options.length));
                if (isBoolCheckbox) {
                  return val === true;
                }

                return !(val === undefined || val === null || val === '' ||
                         (Array.isArray(val) && val.length === 0));
              };

              // Reactive: every render, recompute whether every visible required field
              // on the current page is filled. Drives the submit/continue button's
              // visibility — empty required fields hide the CTA.
              const allRequiredFilled = currentFields.every(field => {
                const result = checkFieldFilled(field);
                return result === null || result === true;
              });

              // Validate required fields on current page before advancing.
              // NOTE: empty-check rules per field type — keep aligned with how each
              // field stores its value (see render functions below).
              const validateCurrentPage = () => {
                for (const field of currentFields) {
                  const result = checkFieldFilled(field);
                  if (result === false) {
                    toast.error(`${field.label} is required`);
                    return false;
                  }
                }
                return true;
              };

              // Evaluate page rules — return target page (1-indexed) if a rule matches, else null
              const evaluatePageRules = () => {
                const rulesForThisPage = pageRules.filter(r => (r.source_page ?? 1) === currentPage);
                for (const rule of rulesForThisPage) {
                  const matchType = rule.match_type || 'all';
                  const conds = rule.conditions || [];
                  if (conds.length === 0) continue;

                  const checkCond = (c) => {
                    const actual = formData[c.field_id];
                    const expected = c.value;
                    const toStr = (v) => v === null || v === undefined ? '' : String(v);
                    const toNum = (v) => {
                      const n = parseFloat(v);
                      return isNaN(n) ? 0 : n;
                    };
                    switch (c.operator) {
                      case 'equals':        return toStr(actual) === toStr(expected);
                      case 'not_equals':    return toStr(actual) !== toStr(expected);
                      case 'contains':      return toStr(actual).toLowerCase().includes(toStr(expected).toLowerCase());
                      case 'not_contains':  return !toStr(actual).toLowerCase().includes(toStr(expected).toLowerCase());
                      case 'greater_than':  return toNum(actual) >  toNum(expected);
                      case 'less_than':     return toNum(actual) <  toNum(expected);
                      case 'greater_equal': return toNum(actual) >= toNum(expected);
                      case 'less_equal':    return toNum(actual) <= toNum(expected);
                      case 'is_empty':      return !actual || (Array.isArray(actual) && actual.length === 0);
                      case 'is_not_empty':  return !!actual && !(Array.isArray(actual) && actual.length === 0);
                      case 'starts_with':   return toStr(actual).toLowerCase().startsWith(toStr(expected).toLowerCase());
                      case 'ends_with':     return toStr(actual).toLowerCase().endsWith(toStr(expected).toLowerCase());
                      default:              return false;
                    }
                  };

                  const matched = matchType === 'any' ? conds.some(checkCond) : conds.every(checkCond);
                  if (matched) {
                    const target = rule.target_page || (currentPage + 1);
                    if (rule.action === 'skip_to') {
                      // Skip the target page, go to the page after it
                      return Math.min(totalPages, target + 1);
                    }
                    if (rule.action === 'jump_to') {
                      return Math.min(totalPages, Math.max(1, target));
                    }
                  }
                }
                return null;
              };

              const goNext = () => {
                if (!validateCurrentPage()) return;
                const ruleTarget = evaluatePageRules();
                const nextPage   = ruleTarget !== null ? ruleTarget : currentPage + 1;
                if (nextPage <= totalPages) {
                  // Push current page onto history for accurate Previous
                  pageHistoryRef.current.push(currentPage);
                  setCurrentPage(nextPage);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              };

              const goBack = () => {
                // Use navigation history stack so Previous goes to the page user actually came from
                const history = pageHistoryRef.current;
                if (history.length > 0) {
                  const prev = history.pop();
                  setCurrentPage(prev);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else if (currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              };

              // Wrap the form submit to run page validation on the last page too
              const wrappedSubmit = (e) => {
                if (!validateCurrentPage()) { e.preventDefault(); return; }
                handleSubmit(e);
              };

              return (
              <form onSubmit={wrappedSubmit} className="grid grid-cols-12 gap-6" onClick={(e) => e.stopPropagation()}>
                {/* Member Selection for Closed Group */}
                {renderMemberSelection()}

                {/* Show form fields only if member is selected (for closed group) or always (for open) */}
                {(!isClosedGroup || selectedMember) && (
                  <>
                    {/* Progress bar + page header (only for multi-page forms) */}
                    {isMultiPage && (
                      <div className="col-span-12 mb-2">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-jakarta text-slate-500">
                            Step {currentPage} of {totalPages}
                          </span>
                          <span className="text-xs font-jakarta text-slate-500">
                            {Math.round((currentPage / totalPages) * 100)}%
                          </span>
                        </div>
                        <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${(currentPage / totalPages) * 100}%` }}
                          />
                        </div>
                        {(pageMeta.title || pageMeta.description) && (
                          <div className="mt-4">
                            {pageMeta.title && (
                              <h3 className="text-xl font-outfit font-semibold text-slate-900">{pageMeta.title}</h3>
                            )}
                            {pageMeta.description && (
                              <p className="text-sm text-slate-600 font-jakarta mt-1">{pageMeta.description}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Render only current page's fields */}
                    {currentFields.map((field) => renderField(field))}

                    {/* Navigation for multi-page (on all pages except last) */}
                    {isMultiPage && !isLastPage && (
                      <div className="col-span-12 flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={goBack}
                          disabled={isFirstPage}
                        >
                          ← Previous
                        </Button>
                        <Button
                          type="button"
                          onClick={goNext}
                          className="bg-primary hover:bg-primary-hover"
                          style={{ backgroundColor: theme.primary_color, color: '#fff' }}
                        >
                          Next →
                        </Button>
                      </div>
                    )}

                    {/* Previous button on last page (before submit) */}
                    {isMultiPage && isLastPage && (
                      <div className="col-span-12 mt-4">
                        <Button type="button" variant="outline" onClick={goBack}>
                          ← Previous
                        </Button>
                      </div>
                    )}
                  </>
                )}
                {(!isClosedGroup || selectedMember) && isLastPage && (
                  <>

                    {/* Coupon Code Section */}
                    {campaign.form_config.base_price > 0 && (
                      <div className="col-span-12 mt-4 pt-4 border-t border-slate-200">
                        <Label className="text-slate-700 font-jakarta font-medium mb-2 block">
                          Have a coupon code?
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Enter coupon code"
                            disabled={couponApplied}
                            className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary uppercase"
                          />
                          {couponApplied ? (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={removeCoupon}
                              className="h-11 px-4"
                            >
                              Remove
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={validateCoupon}
                              disabled={validatingCoupon || !couponCode.trim()}
                              className="h-11 px-4"
                            >
                              {validatingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                            </Button>
                          )}
                        </div>
                        {couponApplied && (
                          <p className="text-sm text-green-600 font-jakarta mt-2">
                            ✓ Coupon &quot;{couponApplied.code}&quot; applied! 
                            ({couponApplied.discount_type === 'percentage' 
                              ? `${couponApplied.discount_value}% off` 
                              : `₹${couponApplied.discount_value} off`})
                          </p>
                        )}
                      </div>
                    )}

                    {/* Price Summary */}
                    {campaign.form_config.base_price > 0 && (
                      <div className="col-span-12 mt-2 pt-4 border-t border-slate-200">
                        <div className="space-y-2">
                          {couponApplied && (
                            <div className="flex items-center justify-between text-sm text-green-600">
                              <span>Discount ({couponApplied.code})</span>
                              <span>
                                -{couponApplied.discount_type === 'percentage' 
                                  ? `${couponApplied.discount_value}%` 
                                  : `₹${couponApplied.discount_value}`}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-between">
                            <span className="text-lg font-outfit font-semibold text-slate-900">Total Amount</span>
                            <span className="text-3xl font-outfit font-bold text-primary">
                              ₹{calculateTotal().toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* For offline-only campaigns, show info message */}
                    {campaign.payment_collection_method === 'offline' && calculateTotal() > 0 && (
                      <div className="col-span-12 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-start gap-3">
                          <Banknote className="w-5 h-5 text-amber-600 mt-0.5" />
                          <div>
                            <p className="font-semibold text-amber-800">Pay at Venue</p>
                            <p className="text-sm text-amber-700 mt-1">
                              This event only accepts cash payment at the venue. Please visit the registration desk to complete your registration and payment.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment Method Selection - Only show if offline payments are enabled via legacy config and amount > 0 */}
                    {calculateTotal() > 0 && campaign.payment_config?.allow_offline_payments && campaign.payment_collection_method !== 'offline' && (
                      <div className="col-span-12 mt-4 pt-4 border-t border-slate-200">
                        <Label className="text-slate-700 font-jakarta font-semibold mb-3 block">
                          Choose Payment Method
                        </Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {/* Online Payment Option */}
                          <button
                            type="button"
                            onClick={() => setPaymentMethod('online')}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                              paymentMethod === 'online'
                                ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                paymentMethod === 'online' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600'
                              }`}>
                                <CreditCard className="w-5 h-5" />
                              </div>
                              <div>
                                <p className={`font-semibold font-jakarta ${
                                  paymentMethod === 'online' ? 'text-primary' : 'text-slate-900'
                                }`}>
                                  Pay Online
                                </p>
                                <p className="text-xs text-slate-500">
                                  Card, UPI, Net Banking
                                </p>
                              </div>
                            </div>
                          </button>

                          {/* Offline Payment Option */}
                          <button
                            type="button"
                            onClick={() => setPaymentMethod('offline')}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                              paymentMethod === 'offline'
                                ? 'border-secondary bg-secondary/5 ring-2 ring-secondary/20'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                paymentMethod === 'offline' ? 'bg-secondary text-white' : 'bg-slate-100 text-slate-600'
                              }`}>
                                <Banknote className="w-5 h-5" />
                              </div>
                              <div>
                                <p className={`font-semibold font-jakarta ${
                                  paymentMethod === 'offline' ? 'text-secondary' : 'text-slate-900'
                                }`}>
                                  Pay at Venue
                                </p>
                                <p className="text-xs text-slate-500">
                                  Cash payment at event
                                </p>
                              </div>
                            </div>
                          </button>
                        </div>
                        
                        {paymentMethod === 'offline' && (
                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg animate-in fade-in slide-in-from-top-2">
                            <p className="text-sm text-amber-800 font-jakarta">
                              <strong>Note:</strong> You&apos;ll receive an OTP via email. Show this OTP at the registration desk to complete your payment in cash.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Submit Button — hidden until every visible required field on this
                        page is filled. A small inline hint takes its place so users know
                        the form will become submittable once they finish. Bottom safe-area
                        inset prevents the iPhone gesture bar from clipping the button. */}
                    {!allRequiredFilled ? (
                      <div className="col-span-12 mt-4 mb-safe">
                        <p className="text-center text-sm text-slate-500 font-jakarta">
                          Fill all required fields to continue
                        </p>
                      </div>
                    ) : (
                    <div className="col-span-12 mt-4 mb-safe">
                      <Button
                        type="submit"
                        disabled={submitting || (isClosedGroup && !selectedMember)}
                        data-testid="submit-registration"
                        /* Mobile: smaller text (16px) + whitespace-normal so long captions
                           like "Continue to Payment - ₹12,345.00" can wrap instead of
                           overflowing past the card edges on a 320px viewport. */
                        className="w-full h-14 sm:h-14 px-4 bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium text-base sm:text-lg !whitespace-normal text-center leading-tight rounded-lg shadow-lg shadow-primary/20 transition-all duration-200 active:scale-95"
                        style={{ 
                          backgroundColor: paymentMethod === 'offline' ? '#14b8a6' : theme.primary_color,
                          color: '#ffffff'
                        }}
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : calculateTotal() > 0 ? (
                          paymentMethod === 'offline' ? (
                            <>
                              <Banknote className="w-5 h-5 mr-2" />
                              Get OTP for Venue Payment
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-5 h-5 mr-2" />
                              Continue to Payment - ₹{calculateTotal().toFixed(2)}
                            </>
                          )
                        ) : (
                          'Complete Free Registration'
                        )}
                      </Button>
                      {calculateTotal() > 0 && paymentMethod === 'online' && (
                        <p className="text-xs text-slate-500 font-jakarta text-center mt-3">
                          Secure payment powered by Razorpay/Instamojo
                        </p>
                      )}
                    </div>
                    )}
                  </>
                )}

                {/* Prompt to select member if closed group */}
                {isClosedGroup && !selectedMember && (
                  <div className="col-span-12 text-center py-8">
                    <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-jakarta">
                      Please select your name from the member list above to continue.
                    </p>
                  </div>
                )}
              </form>
              );
            })() : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 font-jakarta">Registration form is not yet configured.</p>
              </div>
            )}
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm" style={{ color: theme.text_color, opacity: 0.6 }}>
          <p>Powered by Kaizen Events</p>
        </div>
      </div>
    </div>
  );
};

export default PublicRegistration;
